import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { 
  HeartIcon,
  TrashIcon,
  EyeIcon,
  PencilIcon,
  XMarkIcon,
  UserIcon, 
  DocumentTextIcon, 
  UserGroupIcon, 
  SparklesIcon,
  PlusIcon
} from '@heroicons/react/24/outline';
import { useAuthContext } from '../contexts/AuthContext';
import { usePageTitle } from '../hooks/usePageTitle';
import { savedListingsService, SavedListing } from '../services/savedListingsService';
import { businessService, BusinessListing } from '../services/businessService';
import { messageService } from '../services/messageService';

const SavedListings: React.FC = () => {
  const { currentUser, userProfile, loading: authLoading } = useAuthContext();
  const [savedListings, setSavedListings] = useState<Array<SavedListing & { business?: BusinessListing }>>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editNotes, setEditNotes] = useState('');
  const [editTags, setEditTags] = useState<string[]>([]);
  const [newTag, setNewTag] = useState('');
  const [stats, setStats] = useState({
    totalListings: 0,
    activeListings: 0,
    totalMessages: 0,
    unreadMessages: 0,
    savedListings: 0,
    totalViews: 0
  });

  usePageTitle('Saved Listings | BizBuyMax.com');

  useEffect(() => {
    const loadSavedListings = async () => {
      if (!currentUser) return;

      try {
        setLoading(true);
        const saved = await savedListingsService.getSavedListings(currentUser.uid);
        
        // Load business data for each saved listing
        const savedWithBusinesses = await Promise.all(
          saved.map(async (savedListing) => {
            try {
              const business = await businessService.getBusiness(savedListing.businessId);
              return { ...savedListing, business: business || undefined };
            } catch (error) {
              console.error(`Error loading business ${savedListing.businessId}:`, error);
              return { ...savedListing, business: undefined };
            }
          })
        );

        setSavedListings(savedWithBusinesses);

        // Load stats
        const allUserListings = await businessService.getBusinessesByOwner(currentUser.uid);
        const activeListings = allUserListings.filter((listing: any) => 
          listing.basic?.status === 'active' || 
          (listing.basic?.status === 'draft' && listing.payment?.completed === true)
        );
        const conversationsResult = await messageService.getConversationsForUser(currentUser.uid);
        const savedListingsCount = await savedListingsService.getSavedListingsCount(currentUser.uid);
        const totalViews = allUserListings.reduce((sum, listing) => sum + ((listing as any).analytics?.views || 0), 0);
        
        setStats({
          totalListings: activeListings.length,
          activeListings: activeListings.length,
          totalMessages: conversationsResult.length,
          unreadMessages: 0,
          savedListings: savedListingsCount,
          totalViews
        });
      } catch (error) {
        console.error('Error loading saved listings:', error);
      } finally {
        setLoading(false);
      }
    };

    loadSavedListings();
  }, [currentUser]);

  const handleRemove = async (businessId: string) => {
    if (!currentUser || !window.confirm('Are you sure you want to remove this listing from your saved list?')) {
      return;
    }

    try {
      await savedListingsService.removeSavedListing(currentUser.uid, businessId);
      setSavedListings(savedListings.filter(sl => sl.businessId !== businessId));
    } catch (error) {
      console.error('Error removing saved listing:', error);
      alert('Failed to remove listing. Please try again.');
    }
  };

  const handleStartEdit = (savedListing: SavedListing & { business?: BusinessListing }) => {
    setEditingId(savedListing.businessId);
    setEditNotes(savedListing.notes || '');
    setEditTags(savedListing.tags || []);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditNotes('');
    setEditTags([]);
    setNewTag('');
  };

  const handleSaveEdit = async (businessId: string) => {
    if (!currentUser) return;

    try {
      await savedListingsService.updateSavedListing(currentUser.uid, businessId, {
        notes: editNotes,
        tags: editTags
      });

      setSavedListings(savedListings.map(sl => 
        sl.businessId === businessId 
          ? { ...sl, notes: editNotes, tags: editTags }
          : sl
      ));

      handleCancelEdit();
    } catch (error) {
      console.error('Error updating saved listing:', error);
      alert('Failed to update listing. Please try again.');
    }
  };

  const handleAddTag = () => {
    if (newTag.trim() && !editTags.includes(newTag.trim())) {
      setEditTags([...editTags, newTag.trim()]);
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setEditTags(editTags.filter(tag => tag !== tagToRemove));
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'N/A';
    }
  };


  // Render Sidebar
  const renderSidebar = () => (
    <div className="lg:col-span-3 space-y-6">
      {/* Quick Actions */}
      <div className="bg-gradient-to-br from-white via-gray-50 to-gray-100 rounded-2xl shadow-xl border border-gray-200/50 p-6 backdrop-blur-sm">
        <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
          <div className="w-2 h-8 bg-gradient-to-b from-primary-500 to-secondary-500 rounded-full mr-3"></div>
          Quick Actions
        </h3>
        <div className="space-y-3">
          <Link
            to="/sell-business"
            className="flex items-center p-4 text-gray-700 hover:text-primary-700 bg-white hover:bg-gradient-to-r hover:from-primary-50 hover:to-secondary-50 rounded-xl transition-all duration-300 shadow-sm hover:shadow-md border border-gray-100 hover:border-primary-200"
          >
            <div className="p-2 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg mr-4 shadow-sm">
              <PlusIcon className="h-5 w-5 text-white" />
            </div>
            <span className="font-medium">List New Business</span>
          </Link>
          <Link
            to="/account/listings"
            className="flex items-center p-4 text-gray-700 hover:text-blue-700 bg-white hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 rounded-xl transition-all duration-300 shadow-sm hover:shadow-md border border-gray-100 hover:border-blue-200"
          >
            <div className="p-2 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg mr-4 shadow-sm">
              <DocumentTextIcon className="h-5 w-5 text-white" />
            </div>
            <span className="font-medium">Manage Listings</span>
          </Link>
          <Link
            to="/account/leads"
            className="flex items-center p-4 text-gray-700 hover:text-green-700 bg-white hover:bg-gradient-to-r hover:from-green-50 hover:to-emerald-50 rounded-xl transition-all duration-300 shadow-sm hover:shadow-md border border-gray-100 hover:border-green-200"
          >
            <div className="p-2 bg-gradient-to-br from-green-500 to-green-600 rounded-lg mr-4 shadow-sm">
              <UserGroupIcon className="h-5 w-5 text-white" />
            </div>
            <span className="font-medium">View Leads</span>
          </Link>
          <Link
            to="/account/saved-listings"
            className="flex items-center p-4 text-red-700 bg-red-50 hover:bg-gradient-to-r hover:from-red-50 hover:to-pink-50 rounded-xl transition-all duration-300 shadow-sm hover:shadow-md border border-red-200"
          >
            <div className="p-2 bg-gradient-to-br from-red-500 to-red-600 rounded-lg mr-4 shadow-sm">
              <HeartIcon className="h-5 w-5 text-white" />
            </div>
            <span className="font-medium">Saved Listings</span>
          </Link>
          <Link
            to="/account/upgrades"
            className="flex items-center p-4 text-gray-700 hover:text-purple-700 bg-white hover:bg-gradient-to-r hover:from-purple-50 hover:to-violet-50 rounded-xl transition-all duration-300 shadow-sm hover:shadow-md border border-gray-100 hover:border-purple-200"
          >
            <div className="p-2 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg mr-4 shadow-sm">
              <SparklesIcon className="h-5 w-5 text-white" />
            </div>
            <span className="font-medium">Upgrade Account</span>
          </Link>
          <Link
            to="/account/profile"
            className="flex items-center p-4 text-gray-700 hover:text-gray-800 bg-white hover:bg-gradient-to-r hover:from-gray-50 hover:to-slate-50 rounded-xl transition-all duration-300 shadow-sm hover:shadow-md border border-gray-100 hover:border-gray-200"
          >
            <div className="p-2 bg-gradient-to-br from-gray-500 to-gray-600 rounded-lg mr-4 shadow-sm">
              <UserIcon className="h-5 w-5 text-white" />
            </div>
            <span className="font-medium">Edit Profile</span>
          </Link>
        </div>
      </div>

      {/* Account Stats */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Account Statistics</h3>
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">Total Listings</span>
            <span className="font-medium">{stats.totalListings}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">Active Listings</span>
            <span className="font-medium text-green-600">{stats.activeListings}</span>
          </div>
          {/* <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">Total Views</span>
            <span className="font-medium">{stats.totalViews.toLocaleString()}</span>
          </div> */}
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">Leads</span>
            <span className="font-medium">{stats.totalMessages}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">Saved Listings</span>
            <span className="font-medium text-primary-600">{stats.savedListings}</span>
          </div>
          {stats.unreadMessages > 0 && (
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Unread Leads</span>
              <span className="font-medium text-red-600">{stats.unreadMessages}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // Show loading while auth is being checked
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Show sign in message only after auth has loaded and user is not authenticated
  if (!currentUser || !userProfile) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 text-lg mb-4">⚠️ Please sign in to view your saved listings</div>
          <Link to="/signin" className="bg-primary-600 text-white px-4 py-2 rounded hover:bg-primary-700">
            Sign In
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Saved Listings | BizBuyMax.com</title>
        <meta name="description" content="View and manage your saved business listings" />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary-600 via-primary-700 to-secondary-600 shadow-lg">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-white drop-shadow-sm">Saved Listings</h1>
                <p className="text-primary-100 mt-1 text-lg">
                  {savedListings.length} {savedListings.length === 1 ? 'listing' : 'listings'} saved
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 pb-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-9">
              {loading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
                  <p className="mt-4 text-gray-600">Loading saved listings...</p>
                </div>
              ) : savedListings.length === 0 ? (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
                  <HeartIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-xl font-medium text-gray-900 mb-2">No saved listings yet</h3>
                  <p className="text-gray-600 mb-6">Start exploring businesses and save the ones you're interested in</p>
                  <Link
                    to="/businesses"
                    className="inline-flex items-center px-6 py-3 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors font-medium"
                  >
                    Browse Businesses
                  </Link>
                </div>
              ) : (
                <div className="space-y-6">
                  {savedListings.map((savedListing) => {
                const business = savedListing.business;
                const featuredImage = business?.media?.featuredImage || business?.media?.images?.[0];
                const isEditing = editingId === savedListing.businessId;

                return (
                  <div key={savedListing.businessId} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                    <div className="p-6">
                      <div className="flex items-start gap-6">
                        {/* Image */}
                        <Link
                          to={business?.basic?.seoUrl ? `/business/${business.basic.seoUrl}` : `/business/${savedListing.businessId}`}
                          className="w-32 h-32 flex-shrink-0"
                        >
                          {featuredImage ? (
                            <img 
                              src={featuredImage} 
                              alt={business?.basic?.title || 'Business listing'}
                              className="w-full h-full object-cover rounded-lg hover:opacity-90 transition-opacity"
                            />
                          ) : (
                            <div className="w-full h-full bg-gray-200 rounded-lg flex items-center justify-center">
                              <span className="text-xs text-gray-500 font-medium">No Image</span>
                            </div>
                          )}
                        </Link>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex-1">
                              <Link
                                to={business?.basic?.seoUrl ? `/business/${business.basic.seoUrl}` : `/business/${savedListing.businessId}`}
                                className="text-xl font-bold text-primary-600 hover:text-primary-700 transition-colors block"
                              >
                                {business?.basic?.title || 'Business Listing'}
                              </Link>
                              {business && (
                                <p className="text-sm text-gray-600 mt-1">
                                  {business.location.city}, {business.location.province} • ${business.financial.askingPrice.toLocaleString()}
                                </p>
                              )}
                            </div>
                            <div className="flex items-center gap-2 ml-4">
                              <button
                                onClick={() => handleStartEdit(savedListing)}
                                className="p-2 text-gray-400 hover:text-primary-600 transition-colors"
                                title="Edit notes and tags"
                              >
                                <PencilIcon className="h-5 w-5" />
                              </button>
                              <button
                                onClick={() => handleRemove(savedListing.businessId)}
                                className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                                title="Remove from saved"
                              >
                                <TrashIcon className="h-5 w-5" />
                              </button>
                            </div>
                          </div>

                          {/* Notes */}
                          {isEditing ? (
                            <div className="mb-4">
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Notes
                              </label>
                              <textarea
                                value={editNotes}
                                onChange={(e) => setEditNotes(e.target.value)}
                                rows={3}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                                placeholder="Add your notes about this listing..."
                              />
                            </div>
                          ) : (
                            savedListing.notes && (
                              <div className="mb-3">
                                <p className="text-sm text-gray-700 whitespace-pre-wrap">
                                  {savedListing.notes}
                                </p>
                              </div>
                            )
                          )}

                          {/* Tags */}
                          {isEditing ? (
                            <div className="mb-4">
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Tags
                              </label>
                              <div className="flex flex-wrap gap-2 mb-2">
                                {editTags.map((tag, index) => (
                                  <span
                                    key={index}
                                    className="inline-flex items-center gap-1 px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-sm"
                                  >
                                    {tag}
                                    <button
                                      onClick={() => handleRemoveTag(tag)}
                                      className="hover:text-primary-900"
                                    >
                                      <XMarkIcon className="h-4 w-4" />
                                    </button>
                                  </span>
                                ))}
                              </div>
                              <div className="flex gap-2">
                                <input
                                  type="text"
                                  value={newTag}
                                  onChange={(e) => setNewTag(e.target.value)}
                                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                                  placeholder="Add tag..."
                                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                                />
                                <button
                                  onClick={handleAddTag}
                                  className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md text-sm font-medium transition-colors"
                                >
                                  Add
                                </button>
                              </div>
                            </div>
                          ) : (
                            savedListing.tags && savedListing.tags.length > 0 && (
                              <div className="mb-3">
                                <div className="flex flex-wrap gap-2">
                                  {savedListing.tags.map((tag, index) => (
                                    <span
                                      key={index}
                                      className="px-2 py-1 bg-primary-100 text-primary-700 rounded-full text-xs font-medium"
                                    >
                                      {tag}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )
                          )}

                          {/* Saved date */}
                          <div className="flex items-center gap-4 text-xs text-gray-500 mt-3">
                            <span>Saved on {formatDate(savedListing.savedAt)}</span>
                            {business && (
                              <Link
                                to={business.basic.seoUrl ? `/business/${business.basic.seoUrl}` : `/business/${savedListing.businessId}`}
                                className="text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1"
                              >
                                <EyeIcon className="h-4 w-4" />
                                View Listing
                              </Link>
                            )}
                          </div>

                          {/* Edit actions */}
                          {isEditing && (
                            <div className="flex gap-2 mt-4">
                              <button
                                onClick={() => handleSaveEdit(savedListing.businessId)}
                                className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors text-sm font-medium"
                              >
                                Save Changes
                              </button>
                              <button
                                onClick={handleCancelEdit}
                                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors text-sm font-medium"
                              >
                                Cancel
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
                  })}
                </div>
              )}
            </div>
            {renderSidebar()}
          </div>
        </div>
      </div>
    </>
  );
};

export default SavedListings;
